import { TextField, TextFieldProps } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import getTestId from "../../utils/getTestId";

type TextInputExtendedProps = TextFieldProps & {
  label: string;
  index?: number;
};

/*
 * The click dummy's field, shared by every journey control: a 14px label above
 * a 48px outlined box, and 13px help text beneath it.
 *
 * MUI's outlined field floats its label into the border, which is not the
 * approved design and is why a row mixing a select, a read-only value and a
 * text box never lined up. Taking the label out of flow (position: static,
 * transform: none) puts it above the box for all three, at the same height.
 *
 * Exported because three journeys build a TextField directly rather than
 * through this component -- the deposit template's amount/name/email, the
 * deposit wallet-and-phone control, and the configured-fields renderer -- and
 * they must look identical rather than merely similar.
 */
export const fieldSx: SxProps<Theme> = {
  "& .MuiInputLabel-root": {
    position: "static",
    transform: "none",
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.3,
    color: "text.primary",
    mb: 0.75,
    maxWidth: "100%",
    whiteSpace: "normal",
    // The label names the control for a screen reader through its `for`
    // attribute; the asterisk MUI appends stays decorative.
    pointerEvents: "none",
  },
  "& .MuiInputLabel-root.Mui-focused, & .MuiInputLabel-root.Mui-error": {
    color: "text.primary",
  },
  "& .MuiInputLabel-shrink": { transform: "none" },
  "& .MuiOutlinedInput-root": {
    height: 48,
    borderRadius: "10px",
    bgcolor: "background.paper",
    "& fieldset": { borderColor: "divider" },
    "&:hover fieldset": { borderColor: "text.secondary" },
    "&.Mui-focused fieldset": { borderColor: "primary.main", borderWidth: 1 },
    "&.Mui-error fieldset": { borderColor: "error.main" },
  },
  // The outline's notch exists only for a floating label. With the label above
  // the box it would cut a gap in the top border.
  "& .MuiOutlinedInput-notchedOutline legend": { display: "none" },
  "& .MuiFormHelperText-root": {
    fontSize: 13,
    lineHeight: 1.35,
    mt: 0.75,
    ml: 0,
    color: "text.secondary",
  },
  "& .MuiFormHelperText-root.Mui-error": { color: "error.main" },
};

const TextInput = ({ label, index, ...rest }: TextInputExtendedProps) => {
  return (
    <TextField
      label={label}
      data-testid={getTestId({ label, index })}
      {...rest}
      sx={{ ...fieldSx, ...rest.sx }}
    />
  );
};

export default TextInput;
